import React from "react";
import { cn } from "@/lib/utils";

export type SettingsNavItem = {
  value: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  count?: number;
  /** Renders in the danger tone (e.g. a log-out / delete row). */
  danger?: boolean;
};

export type SettingsNavGroup = {
  /** Optional uppercase group heading (e.g. the workspace / account name). */
  label?: React.ReactNode;
  items: SettingsNavItem[];
};

type Props = {
  groups: SettingsNavGroup[];
  value: string;
  onSelect: (value: string) => void;
  ariaLabel?: string;
  className?: string;
};

/** Vertical settings navigation rail — group labels + rows, active row filled
 *  soft-indigo with brand text. Collapses to a horizontal scroller on mobile.
 *  Ported from the prototype's in-sidebar `.subnav`. */
export function SettingsNav({ groups, value, onSelect, ariaLabel, className }: Props) {
  const navRef = React.useRef<HTMLElement>(null);

  // On the narrow rail (a horizontal scroller) the active item can sit
  // off-screen — you land on ?tab=billing and the strip still shows "General".
  React.useEffect(() => {
    // `scrollIntoView` with both axes "nearest" is deliberate over a measured
    // scrollLeft: it is a no-op when the item is already visible (so it costs
    // nothing on the desktop rail) and needs no overflow check of our own.
    // "block: nearest" is what keeps the page itself from moving. Instant, not
    // smooth: this fires on mount/route change, where the rail should already
    // be in position rather than animating into it.
    navRef.current
      ?.querySelector<HTMLElement>(".vb-settingsnav__item--active")
      ?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
  }, [value]);

  return (
    <nav ref={navRef} className={cn("vb-settingsnav", className)} aria-label={ariaLabel}>
      {groups.map((g) => (
        // Stable key from the group's item set (groups have no id; index keys
        // are the flagged anti-pattern even for static config).
        <React.Fragment key={g.items.map((i) => i.value).join("|")}>
          {g.label != null && <div className="vb-settingsnav__group">{g.label}</div>}
          {g.items.map((it) => {
            const active = it.value === value;
            return (
              <button
                key={it.value}
                type="button"
                className={cn(
                  "vb-settingsnav__item",
                  active && "vb-settingsnav__item--active",
                  it.danger && "vb-settingsnav__item--danger",
                )}
                aria-current={active ? "page" : undefined}
                onClick={() => onSelect(it.value)}
              >
                {it.icon != null && (
                  <span className="vb-settingsnav__ic" aria-hidden="true">
                    {it.icon}
                  </span>
                )}
                {it.label}
                {it.count != null && <span className="vb-settingsnav__ct">{it.count}</span>}
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </nav>
  );
}

export default SettingsNav;
